/** Subscriber sidebar gates that must match API RBAC, not just marketing nav.
 *  Keep this module in the production website image so sales CRM / Pursuit
 *  nav cannot be skip-build retagged from an older website-* digest.
 *
 *  Job roles (admin / contributor / viewer) are separate from plan entitlements.
 *  See docs/operations/subscription_membership_rbac.md.
 */

const normalizePermissions = (permissions?: Array<string | null | undefined>): string[] =>
  (permissions || []).map((permission) => String(permission || '').trim().toLowerCase());

const hasWildcard = (permissions: string[]): boolean => permissions.includes('*');

export function canAccessCrmNav(options: {
  isPlatformAdmin?: boolean;
  permissions?: Array<string | null | undefined>;
}): boolean {
  // Same check as the Pursuit API. The admin flag alone must not open the page.
  const permissions = normalizePermissions(options.permissions);
  return hasWildcard(permissions) || permissions.includes('crm.pipeline.read');
}

export function canAccessRazzmatazzNav(options: {
  isPlatformAdmin?: boolean;
  permissions?: Array<string | null | undefined>;
}): boolean {
  const permissions = normalizePermissions(options.permissions);
  return hasWildcard(permissions) || permissions.includes('razzmatazz.workspace.read');
}

export function canAccessInletNav(options: {
  isPlatformAdmin?: boolean;
  permissions?: Array<string | null | undefined>;
}): boolean {
  const permissions = normalizePermissions(options.permissions);
  return hasWildcard(permissions) || permissions.includes('inlet.workspace.read');
}

/** The server refused the role. The menu should not keep the page open. */
export function isRoleAccessDeniedMessage(message: string): boolean {
  const text = String(message || '').toLowerCase();
  return text.includes('missing required rbac') || text.includes('admin role is required');
}

/** Subscription admin only: Stripe portal / payment methods.
 *  NextCompli admin (platform wildcard) does not grant this.
 */
export function canManageBillingNav(options: {
  isPlatformAdmin?: boolean;
  permissions?: Array<string | null | undefined>;
}): boolean {
  const permissions = normalizePermissions(options.permissions);
  return (
    permissions.includes('account.payments.manage')
    || permissions.includes('users.company.manage')
  );
}

/** Admin + contributor: mutate projects / compliance / OFAC data. */
export function canWriteWorkspaceNav(options: {
  isPlatformAdmin?: boolean;
  permissions?: Array<string | null | undefined>;
}): boolean {
  if (options.isPlatformAdmin) {
    return true;
  }
  const permissions = normalizePermissions(options.permissions);
  return hasWildcard(permissions) || permissions.includes('audit.workspace.write');
}

export function canAccessEntitledProductNav(options: {
  isPlatformAdmin?: boolean;
  permissions?: Array<string | null | undefined>;
  entitlement: string;
  companyHasEntitlement: boolean;
}): boolean {
  if (options.isPlatformAdmin) {
    return true;
  }
  if (!options.companyHasEntitlement) {
    return false;
  }
  const permissions = normalizePermissions(options.permissions);
  const entitlement = String(options.entitlement || '').trim().toLowerCase();
  return hasWildcard(permissions) || permissions.includes(`feature.${entitlement}.use`);
}
